import { useI18n } from "@wojo/localization";
import { StoryblokImage } from "@wojo/storyblok";
import { Button, Markdown, Modal, ModalBody, ModalProps, Text } from "@wojo/ui";
import React from "react";
import { ContentAsset } from "../../../../generated/graphql";
import styles from "./SpecialDepartureModal.module.scss";

export type SpecialDepartureModalProps = Pick<
    ModalProps,
    "isOpen" | "onCloseRequested"
> & {
    icon?: ContentAsset | null;
    tourDescription?: string | null;
    tourImage?: ContentAsset | null;
    tourName?: string | null;
    tourPagePath?: string | null;
    colorPalette?: string | null;
};

export const SpecialDepartureModal: React.FC<SpecialDepartureModalProps> = ({
    icon,
    tourDescription,
    tourImage,
    tourName,
    tourPagePath,
    colorPalette,
    ...modalProps
}) => {
    const { productPage } = useI18n("productPage");
    const tagline = productPage.tripBuilder.quote.specialDeparture.tagline.l();
    const isFallbackDisplay = !tourImage?.filename;
    const modalBackgroundColor =
        isFallbackDisplay && colorPalette
            ? `var(--s-color-${colorPalette}-quiet)`
            : undefined;

    return (
        <Modal
            {...modalProps}
            accessibleTitle={`${tagline}: ${tourName}`}
            style={{
                backgroundColor: modalBackgroundColor,
            }}
        >
            {!!tourImage?.filename && (
                <StoryblokImage
                    className={styles["modal-image"]}
                    mobile={{ asset: tourImage, size: { width: 1534 } }}
                    desktop={{ asset: tourImage, size: { width: 996 } }}
                />
            )}
            {isFallbackDisplay && !!icon?.filename && (
                <StoryblokImage
                    style={{
                        display: "flex",
                        margin: "0 auto var(--g-spacing-md)",
                    }}
                    mobile={{ asset: icon, size: { width: 42 } }}
                />
            )}
            <ModalBody style={{ paddingBottom: "var(--g-spacing-md)" }}>
                <Text.Heading
                    order="4"
                    tag="h2"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "var(--g-spacing-md)",
                        alignItems: "center",
                    }}
                >
                    <Text.Detail tag="span">{tagline}</Text.Detail>
                    {tourName}
                </Text.Heading>
            </ModalBody>
            <ModalBody
                style={{
                    paddingTop: "var(--g-spacing-md)",
                    backgroundColor: "var(--s-color-surface-secondary)",
                }}
            >
                {!!tourDescription && <Markdown markdown={tourDescription} />}
                {!!tourPagePath && (
                    <div
                        style={{
                            marginTop: "var(--g-spacing-xl)",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Button variant="secondary" tag="a" href={tourPagePath}>
                            {productPage.tripBuilder.quote.specialDeparture.ctaLabel.l()}
                        </Button>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};
